import React from 'react';
import styled from 'styled-components';
import { VOLUNTEER_CATEGORIES } from '../constants/volunteerCategories';
import { useVolunteerCategories } from '../hooks/useVolunteerCategories';
import {
  EventRequest,
  EventRequestTicketTypeDraft,
  useCreateEventRequest,
  useMyEventRequests,
  useUpdateMyEventRequest,
} from '../hooks/useEventRequests';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { supabase } from '../services/supabase';
import { buildPublicFacilityBookingUrl } from '../utils/publicSiteUrls';

const Page = styled.div`
  min-height: 100%;
  color: var(--onSurface);
  padding: clamp(1.25rem, 4vw, 2.5rem) clamp(1rem, 3vw, 2rem) 3rem;
  background: linear-gradient(165deg, var(--surfaceVariant) 0%, var(--background) 38%, var(--background) 100%);
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: clamp(1.25rem, 3vw, 2rem);
`;

const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const Eyebrow = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--primary);
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.65rem, 4vw, 2.15rem);
  font-weight: 700;
  line-height: 1.2;
  color: var(--onSurface);
  letter-spacing: -0.02em;
`;

const Intro = styled.p`
  margin: 0;
  max-width: 52ch;
  color: var(--onSurfaceVariant);
  line-height: 1.6;
  font-size: 1rem;
`;

const TabShell = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 0.25rem;
  border-radius: 14px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.04) inset;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  padding: 0.55rem 1.05rem;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition:
    background 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease;

  &[data-active='true'] {
    background: var(--primary);
    color: var(--onPrimary);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  }

  &:hover:not([data-active='true']) {
    color: var(--onSurface);
    background: rgba(255, 255, 255, 0.04);
  }
`;

const Form = styled.form`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 20px;
  padding: clamp(1rem, 2.5vw, 1.4rem) clamp(1.1rem, 2.5vw, 1.65rem);
  display: flex;
  flex-direction: column;
  gap: clamp(0.75rem, 1.5vw, 1rem);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.2),
    0 20px 50px -24px rgba(0, 0, 0, 0.55);
`;

/* Use div, not section: global index.css targets `section { padding: 90px 0 }`. */
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const FormSectionTitle = styled.h2`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const FormDivider = styled.div`
  height: 1px;
  margin: 0.35rem 0;
  background: linear-gradient(90deg, var(--outline) 0%, transparent 100%);
  opacity: 0.85;
`;

/** Full-width row in the basics grid: ticket type drafts directly under the ticketed toggle. */
const TicketTypesBlock = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.85rem 1rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--onSurface);
`;

const DropdownList = styled.ul`
  list-style: none;
  margin: 0.4rem 0 0;
  padding: 0.25rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  max-height: 200px;
  overflow: auto;
  background: var(--elevation3, var(--surface));
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
`;

const DropdownItem = styled.li`
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  color: var(--onSurface);
  font-weight: 500;
  font-size: 0.9rem;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const DropdownEmpty = styled.li`
  padding: 0.65rem 0.75rem;
  color: var(--onSurfaceVariant);
  font-weight: 500;
  font-size: 0.88rem;
`;

const controlFocus = `
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(215, 196, 173, 0.22);
`;

const Input = styled.input`
  padding: 0.65rem 0.85rem;
  min-height: 2.75rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  font-size: 0.95rem;
  font-family: inherit;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;

  &::placeholder {
    color: var(--onSurfaceVariant);
  }

  &:hover:not(:disabled) {
    border-color: var(--outlineVariant);
  }

  &:focus-visible {
    ${controlFocus}
    background: var(--surface);
  }

  &:disabled {
    opacity: 0.72;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 0.65rem 0.85rem;
  min-height: 2.75rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  font-size: 0.95rem;
  font-family: inherit;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23B0BEC5' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.85rem center;
  padding-right: 2.25rem;

  &:hover {
    border-color: var(--outlineVariant);
  }

  &:focus-visible {
    ${controlFocus}
    background-color: var(--surface);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 0.85rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  min-height: 110px;
  font-size: 0.95rem;
  font-family: inherit;
  line-height: 1.5;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  resize: vertical;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;

  &::placeholder {
    color: var(--onSurfaceVariant);
  }

  &:hover {
    border-color: var(--outlineVariant);
  }

  &:focus-visible {
    ${controlFocus}
    background: var(--surface);
  }
`;

const Helper = styled.span`
  font-weight: 400;
  color: var(--onSurfaceVariant);
  font-size: 0.85rem;
  line-height: 1.45;
`;

const TicketToggleCard = styled.div`
  grid-column: 1 / -1;
  padding: 0.75rem 0.95rem;
  border-radius: 14px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const TicketToggleTitle = styled.span`
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--onSurface);
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  color: var(--onSurface);
  font-weight: 500;
  font-size: 0.92rem;
  line-height: 1.45;
  cursor: pointer;
`;

const CheckboxInput = styled.input`
  width: 1.1rem;
  height: 1.1rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
  accent-color: var(--primary);
  cursor: pointer;
`;

const TicketSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const TicketCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 0.85rem 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: linear-gradient(145deg, var(--surfaceVariant) 0%, var(--elevation2, #222) 100%);
`;

const TicketCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const TicketCardTitle = styled.h4`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--onSurface);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.85rem;
  align-items: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  background: var(--primary);
  color: var(--onPrimary);
  border: 1px solid var(--primaryDark);
  border-radius: 12px;
  padding: 0.7rem 1.35rem;
  font-weight: 700;
  font-size: 0.95rem;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease,
    filter 0.12s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.32);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Secondary = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
  line-height: 1.45;
  max-width: 36ch;
`;

const ImagePreview = styled.img`
  width: 100%;
  max-width: 320px;
  border-radius: 14px;
  border: 1px solid var(--outline);
  object-fit: cover;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;

const VisuallyHiddenFile = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const FileUploadRow = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.2rem;
`;

const FileUploadTrigger = styled.label<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.05rem;
  border-radius: 12px;
  border: 1px dashed var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: var(--surfaceVariant);
  }

  ${({ $disabled }) =>
    $disabled
      ? `
    opacity: 0.55;
    pointer-events: none;
    cursor: not-allowed;
  `
      : ''}
`;

const FormFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0;
  padding-top: 0.75rem;
`;

const MiniSecondaryButton = styled.button`
  align-self: flex-start;
  border: 1px solid var(--outline);
  background: transparent;
  color: var(--onSurface);
  border-radius: 10px;
  padding: 0.4rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
    border-color: var(--primary);
    color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Status = styled.div<{ $variant: 'success' | 'error' }>`
  border-radius: 14px;
  padding: 0.85rem 1rem 0.85rem 1.1rem;
  border: 1px solid ${({ $variant }) => ($variant === 'success' ? 'var(--success)' : 'var(--error)')};
  border-left-width: 4px;
  background: ${({ $variant }) => ($variant === 'success' ? 'var(--successContainer)' : 'var(--errorContainer)')};
  color: ${({ $variant }) => ($variant === 'success' ? 'var(--onSuccessContainer)' : 'var(--onErrorContainer)')};
  font-weight: 600;
  font-size: 0.92rem;
  line-height: 1.45;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const SubmissionsPanel = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 20px;
  padding: clamp(1.1rem, 2.5vw, 1.65rem);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.2),
    0 20px 50px -24px rgba(0, 0, 0, 0.55);
`;

const PanelTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const EmptyState = styled.div`
  color: var(--onSurfaceVariant);
  font-weight: 500;
  padding: 1.5rem 0.5rem;
  text-align: center;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const SubmissionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SubmissionCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 16px;
  background: var(--surfaceVariant);
  padding: 1.1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;

  &:hover {
    border-color: rgba(215, 196, 173, 0.35);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
    transform: translateY(-1px);
  }
`;

const SubmissionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.8rem;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const SubmissionTitle = styled.h3`
  margin: 0 0 0.2rem 0;
  font-family: 'Poppins', sans-serif;
  color: var(--onSurface);
  font-size: 1.08rem;
  font-weight: 600;
  line-height: 1.3;
`;

const SubmissionMeta = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.84rem;
`;

const SubmissionStatus = styled.span`
  border: 1px solid var(--outline);
  background: rgba(0, 0, 0, 0.2);
  color: var(--onSurface);
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: 0.02em;
`;

const SubmissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.55rem 1rem;
`;

const SubmissionField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const SubmissionLabel = styled.span`
  font-size: 0.7rem;
  color: var(--onSurfaceVariant);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SubmissionValue = styled.span`
  color: var(--onSurface);
  font-size: 0.9rem;
  word-break: break-word;
  line-height: 1.4;
`;

const SubmissionDescription = styled.p`
  margin: 0;
  color: var(--onSurface);
  line-height: 1.5;
  white-space: pre-wrap;
  font-size: 0.9rem;
`;

const SubmissionImagePreview = styled.img`
  width: 100%;
  max-width: 320px;
  border-radius: 14px;
  border: 1px solid var(--outline);
  object-fit: cover;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;

const SubmissionImageButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: zoom-in;
  width: fit-content;
  border-radius: 14px;
  overflow: hidden;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
  }
`;

const SubmissionFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 1rem;
`;

const SubmissionFilterButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  padding: 0.38rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover:not([data-active='true']) {
    color: var(--onSurface);
    border-color: var(--outlineVariant);
  }

  &[data-active='true'] {
    background: var(--primary);
    color: var(--onPrimary);
    border-color: var(--primaryDark);
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.76);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: min(1100px, 95vw);
  max-height: 92vh;
`;

const LightboxImage = styled.img`
  display: block;
  width: auto;
  max-width: 95vw;
  max-height: 92vh;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  object-fit: contain;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
`;

const LightboxCloseButton = styled.button`
  position: absolute;
  top: -0.7rem;
  right: -0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 999px;
  width: 2rem;
  height: 2rem;
  font-size: 1rem;
  font-weight: 800;
  color: #fff;
  background: rgba(0, 0, 0, 0.65);
  cursor: pointer;
`;

type FormState = {
  title: string;
  description: string;
  ministry: string;
  event_type: 'Event' | 'General Announcement';
  location: string;
  start_date: string;
  start_time: string;
  share_date: string;
  image_url: string;
  is_ticketed: boolean;
};

type TicketTypeForm = {
  id: string;
  type: string;
  description: string;
  price: string;
};

const initialState: FormState = {
  title: '',
  description: '',
  ministry: '',
  event_type: 'Event',
  location: '',
  start_date: '',
  start_time: '',
  share_date: '',
  image_url: '',
  is_ticketed: false,
};

const STATUS_FILTER_ORDER = [
  'pending',
  'approved',
  'needs_info',
  'contact_requestor',
  'on_hold',
  'completed',
  'declined',
] as const;

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  needs_info: 'Needs Info',
  contact_requestor: 'Contact Requestor',
  on_hold: 'On Hold',
  completed: 'Completed',
  declined: 'Declined',
};

const formatDisplayDate = (value?: string | null) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

const formatDisplayTime = (value?: string | null) => {
  if (!value) return '—';
  const cleaned = value.trim();
  const amPmMatch = cleaned.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (amPmMatch) return `${amPmMatch[1]}:${amPmMatch[2]} ${amPmMatch[3].toUpperCase()}`;
  const hhmmMatch = cleaned.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);
  if (!hhmmMatch) return value;
  const hours = Number(hhmmMatch[1]);
  const minutes = hhmmMatch[2];
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHour}:${minutes} ${suffix}`;
};

const createEmptyTicketType = (): TicketTypeForm => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: '',
  description: '',
  price: '',
});

const parsePriceToCents = (value: string) => {
  const normalized = value.trim();
  if (!normalized) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
};

const formatPriceInput = (value: number) => (Number.isFinite(value) ? (value / 100).toFixed(2) : '');

const normalizeEventType = (value?: string | null): FormState['event_type'] =>
  value === 'General Announcement' ? 'General Announcement' : 'Event';

const createFacilityRedirectHandoff = async (eventRequestId: string) => {
  const { data, error } = await supabase.functions.invoke('create-event-facility-handoff', {
    body: { eventRequestId },
  });
  if (error) throw error;
  if (data && (data as { error?: string }).error) {
    throw new Error((data as { error: string }).error);
  }

  const token = String((data as { data?: { token?: string } })?.data?.token || '').trim();
  if (!token) {
    throw new Error('Could not create the facility handoff.');
  }
  return token;
};

const EventSubmissionPage: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<'submit' | 'submitted'>('submit');
  const [submittedStatusFilter, setSubmittedStatusFilter] = React.useState<string>('all');
  const [lightboxImage, setLightboxImage] = React.useState<{ url: string; alt: string } | null>(null);
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const [form, setForm] = React.useState<FormState>(initialState);
  const [ticketTypes, setTicketTypes] = React.useState<TicketTypeForm[]>([]);
  const [continueToFacilityRequest, setContinueToFacilityRequest] = React.useState(false);
  const [editingRequestId, setEditingRequestId] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const { mutateAsync: submitRequest, isPending } = useCreateEventRequest();
  const { mutateAsync: updateMyRequest, isPending: isUpdatingRequest } = useUpdateMyEventRequest();
  const { data: volunteerCategories } = useVolunteerCategories();
  const [isMinistryOpen, setIsMinistryOpen] = React.useState(false);
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);
  const announcementFileInputId = React.useId();

  const ministryOptions = React.useMemo(() => {
    const categories =
      volunteerCategories && volunteerCategories.length > 0
        ? volunteerCategories.map((category) => category.label).filter(Boolean)
        : VOLUNTEER_CATEGORIES.map((category) => category.label).filter(Boolean);
    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }, [volunteerCategories]);

  const filteredMinistryOptions = React.useMemo(() => {
    const query = form.ministry.trim().toLowerCase();
    if (!query) return ministryOptions;
    return ministryOptions.filter((option) => option.toLowerCase().includes(query));
  }, [form.ministry, ministryOptions]);
  const isGeneralAnnouncement = form.event_type === 'General Announcement';

  const contactName = React.useMemo(
    () =>
      profile?.full_name ??
      (typeof user?.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : null) ??
      user?.email ??
      'Unknown requester',
    [profile?.full_name, user?.email, user?.user_metadata?.full_name]
  );

  const contactEmail =
    (typeof user?.email === 'string' && user.email) ||
    (typeof user?.user_metadata?.email === 'string' && user.user_metadata.email) ||
    null;
  const requesterId = user?.id ?? null;

  const {
    data: submittedRequests = [],
    isLoading: isLoadingSubmittedRequests,
  } = useMyEventRequests({ requesterId, contactEmail });

  const updateField =
    (field: Exclude<keyof FormState, 'is_ticketed'>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const eventType = normalizeEventType(e.target.value);
    if (eventType === 'General Announcement') {
      setForm((prev) => ({
        ...prev,
        event_type: eventType,
        location: '',
        start_date: '',
        start_time: '',
        is_ticketed: false,
      }));
      setTicketTypes([]);
      setContinueToFacilityRequest(false);
      return;
    }

    setForm((prev) => ({ ...prev, event_type: eventType }));
  };

  const updateTicketTypeField =
    (id: string, field: keyof Omit<TicketTypeForm, 'id'>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setTicketTypes((prev) => prev.map((ticket) => (ticket.id === id ? { ...ticket, [field]: value } : ticket)));
    };

  const handleAddTicketType = () => {
    setTicketTypes((prev) => [...prev, createEmptyTicketType()]);
  };

  const handleRemoveTicketType = (id: string) => {
    setTicketTypes((prev) => prev.filter((ticket) => ticket.id !== id));
  };

  const uploadAnnouncementImage = async (file: File) => {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `event-request-images/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleAnnouncementImageUpload = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage('Please upload an image smaller than 5MB.');
      return;
    }

    setErrorMessage(null);
    setIsUploadingImage(true);
    try {
      const imageUrl = await uploadAnnouncementImage(file);
      setForm((prev) => ({ ...prev, image_url: imageUrl }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not upload image.';
      setErrorMessage(message);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!form.title.trim()) {
      setErrorMessage('Title is required.');
      return;
    }
    if (!form.ministry.trim()) {
      setErrorMessage('Ministry/Department is required.');
      return;
    }
    if (!ministryOptions.includes(form.ministry.trim())) {
      setErrorMessage('Please choose a Ministry/Department from the list.');
      return;
    }
    if (!isGeneralAnnouncement && !form.start_date) {
      setErrorMessage('Start date is required.');
      return;
    }
    if (!isGeneralAnnouncement && form.is_ticketed) {
      if (ticketTypes.length === 0) {
        setErrorMessage('Add at least one ticket type for ticketed events.');
        return;
      }
      for (let index = 0; index < ticketTypes.length; index += 1) {
        const ticket = ticketTypes[index];
        if (!ticket.type.trim()) {
          setErrorMessage(`Ticket type #${index + 1} needs a name.`);
          return;
        }
        const priceCents = parsePriceToCents(ticket.price);
        if (priceCents === null) {
          setErrorMessage(`Ticket type #${index + 1} needs a valid non-negative price.`);
          return;
        }
      }
    }

    try {
      const normalizedTicketTypes: EventRequestTicketTypeDraft[] = !isGeneralAnnouncement && form.is_ticketed
        ? ticketTypes.map((ticket, index) => ({
            name: ticket.type.trim(),
            description: ticket.description.trim() || null,
            price_cents: parsePriceToCents(ticket.price) ?? 0,
            currency: 'USD',
            sort_order: index,
          }))
        : [];

      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        image_url: form.image_url?.trim() || null,
        ministry: form.ministry?.trim() || null,
        event_type: form.event_type?.trim() || null,
        location: isGeneralAnnouncement ? null : form.location?.trim() || null,
        start_date: isGeneralAnnouncement ? null : form.start_date || null,
        start_time: isGeneralAnnouncement ? null : form.start_time || null,
        share_date: form.share_date || null,
        contact_name: contactName,
        contact_email: contactEmail,
        requester_id: requesterId,
        is_ticketed: isGeneralAnnouncement ? false : form.is_ticketed,
        ticket_types: normalizedTicketTypes,
      };

      if (editingRequestId) {
        if (!requesterId && !contactEmail) {
          setErrorMessage('Could not determine your account identity. Please sign in again.');
          return;
        }
        await updateMyRequest({
          id: editingRequestId,
          requesterId,
          contactEmail,
          ...payload,
        });
        setSuccessMessage('Your submission has been updated.');
        setEditingRequestId(null);
        setContinueToFacilityRequest(false);
      } else {
        const createdRequest = await submitRequest(payload);
        if (continueToFacilityRequest) {
          try {
            const handoffToken = await createFacilityRedirectHandoff(createdRequest.id);
            window.location.assign(buildPublicFacilityBookingUrl(handoffToken));
            return;
          } catch (handoffError) {
            const handoffMessage =
              handoffError instanceof Error
                ? handoffError.message
                : 'Could not open the facility request form automatically.';
            setSuccessMessage('Your event has been submitted for approval.');
            setErrorMessage(`${handoffMessage} You can still book the facility separately.`);
          }
        } else {
          setSuccessMessage('Your event has been submitted for approval.');
        }
      }
      setForm(initialState);
      setTicketTypes([]);
      setContinueToFacilityRequest(false);
      setActiveTab('submitted');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not submit the event.';
      setErrorMessage(message);
    }
  };

  const beginEditSubmission = (request: EventRequest) => {
    const mappedTicketTypes =
      request.ticket_types?.map((ticket, index) => ({
        id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
        type: ticket.name || '',
        description: ticket.description || '',
        price: formatPriceInput(
          typeof ticket.price_cents === 'number' ? ticket.price_cents : Number(ticket.price_cents) || 0
        ),
      })) ?? [];
    const eventType = normalizeEventType(request.event_type);
    const isTicketed = eventType === 'Event' && Boolean(request.is_ticketed);

    setForm({
      title: request.title || '',
      description: request.description || '',
      ministry: request.ministry || '',
      event_type: eventType,
      location: request.location || '',
      start_date: request.start_date || '',
      start_time: request.start_time || '',
      share_date: request.share_date || '',
      image_url: request.image_url || '',
      is_ticketed: isTicketed,
    });
    setTicketTypes(isTicketed ? (mappedTicketTypes.length > 0 ? mappedTicketTypes : [createEmptyTicketType()]) : []);
    setEditingRequestId(request.id);
    setSuccessMessage(null);
    setErrorMessage(null);
    setContinueToFacilityRequest(false);
    setActiveTab('submit');
  };

  const statusFilterOptions = React.useMemo(() => {
    const counts = submittedRequests.reduce<Record<string, number>>((acc, req) => {
      const key = req.status || 'pending';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const orderedStatuses = STATUS_FILTER_ORDER.filter((status) => counts[status] > 0);
    const remainingStatuses = Object.keys(counts)
      .filter((status) => !orderedStatuses.includes(status as (typeof STATUS_FILTER_ORDER)[number]))
      .sort((a, b) => a.localeCompare(b));

    const allStatuses = [...orderedStatuses, ...remainingStatuses];
    return [
      { key: 'all', label: 'All', count: submittedRequests.length },
      ...allStatuses.map((status) => ({
        key: status,
        label: STATUS_LABELS[status] ?? status.replace(/_/g, ' '),
        count: counts[status] ?? 0,
      })),
    ];
  }, [submittedRequests]);

  const filteredSubmittedRequests = React.useMemo(() => {
    if (submittedStatusFilter === 'all') return submittedRequests;
    return submittedRequests.filter((request) => (request.status || 'pending') === submittedStatusFilter);
  }, [submittedRequests, submittedStatusFilter]);

  React.useEffect(() => {
    if (!lightboxImage) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightboxImage(null);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxImage]);

  return (
    <Page>
      <Container>
        <PageHeader>
          <Eyebrow>Events</Eyebrow>
          <PageTitle>Event submission</PageTitle>
          <Intro>
            Request a church event in one place. Your submission goes to the approval queue and appears on
            the Events calendar after review.
          </Intro>
        </PageHeader>

        <TabShell>
          <Tabs>
            <TabButton
              type="button"
              data-active={activeTab === 'submit'}
              onClick={() => setActiveTab('submit')}
            >
              {editingRequestId ? 'Edit submission' : 'New submission'}
            </TabButton>
            <TabButton
              type="button"
              data-active={activeTab === 'submitted'}
              onClick={() => setActiveTab('submitted')}
            >
              Submitted ({submittedRequests.length})
            </TabButton>
          </Tabs>
        </TabShell>

        {successMessage ? <Status $variant="success">{successMessage}</Status> : null}
        {errorMessage ? <Status $variant="error">{errorMessage}</Status> : null}

        {activeTab === 'submit' ? (
        <Form onSubmit={handleSubmit}>
          <FormSection>
            <FormSectionTitle>Basics & schedule</FormSectionTitle>
            <Grid>
          <Field>
            Title*
            <Input
              type="text"
              placeholder="Event title"
              value={form.title}
              onChange={updateField('title')}
              required
            />
          </Field>
          <Field>
            Ministry / Department
            <Input
              type="text"
              placeholder="e.g., Youth, Worship"
              value={form.ministry}
              onChange={updateField('ministry')}
              onFocus={() => setIsMinistryOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setIsMinistryOpen(false), 120);
              }}
              required
            />
            {isMinistryOpen ? (
              <DropdownList>
                {filteredMinistryOptions.length > 0 ? (
                  filteredMinistryOptions.map((label) => (
                    <DropdownItem
                      key={label}
                      onMouseDown={() => {
                        setForm((prev) => ({ ...prev, ministry: label }));
                      }}
                    >
                      {label}
                    </DropdownItem>
                  ))
                ) : (
                  <DropdownEmpty>No matches. Choose a valid category.</DropdownEmpty>
                )}
              </DropdownList>
            ) : null}
          </Field>
          <Field>
            Event Type
            <Select value={form.event_type} onChange={handleEventTypeChange}>
              <option value="General Announcement">General Announcement</option>
              <option value="Event">Event</option>
            </Select>
          </Field>
          {!isGeneralAnnouncement ? (
            <TicketToggleCard>
              <TicketToggleTitle>Ticketed event</TicketToggleTitle>
              <CheckboxRow>
                <CheckboxInput
                  type="checkbox"
                  checked={form.is_ticketed}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({ ...prev, is_ticketed: checked }));
                    setTicketTypes((prev) => {
                      if (!checked) return [];
                      return prev.length > 0 ? prev : [createEmptyTicketType()];
                    });
                  }}
                />
                <span>Yes — draft ticket types for Event Setup (name, optional description, price).</span>
              </CheckboxRow>
              <Helper>Staff can refine tickets after approval.</Helper>
            </TicketToggleCard>
          ) : null}
          {!isGeneralAnnouncement && form.is_ticketed ? (
            <TicketTypesBlock>
              <FormSectionTitle as="h3">Ticket types</FormSectionTitle>
              <TicketSection>
                {ticketTypes.map((ticket, index) => (
                  <TicketCard key={ticket.id}>
                    <TicketCardHeader>
                      <TicketCardTitle>Ticket type {index + 1}</TicketCardTitle>
                      <MiniSecondaryButton
                        type="button"
                        onClick={() => handleRemoveTicketType(ticket.id)}
                        disabled={ticketTypes.length === 1}
                      >
                        Remove
                      </MiniSecondaryButton>
                    </TicketCardHeader>
                    <Field>
                      Name
                      <Input
                        type="text"
                        placeholder="General Admission"
                        value={ticket.type}
                        onChange={updateTicketTypeField(ticket.id, 'type')}
                      />
                    </Field>
                    <Field>
                      Description
                      <TextArea
                        placeholder="Optional details"
                        value={ticket.description}
                        onChange={updateTicketTypeField(ticket.id, 'description')}
                      />
                    </Field>
                    <Field>
                      Price (USD)*
                      <Input
                        type="text"
                        placeholder="0.00"
                        inputMode="decimal"
                        value={ticket.price}
                        onChange={updateTicketTypeField(ticket.id, 'price')}
                      />
                    </Field>
                  </TicketCard>
                ))}
                <MiniSecondaryButton type="button" onClick={handleAddTicketType}>
                  Add ticket type
                </MiniSecondaryButton>
              </TicketSection>
            </TicketTypesBlock>
          ) : null}
          {!isGeneralAnnouncement ? (
            <>
              <Field>
                Location
                <Input
                  type="text"
                  placeholder="Campus or venue"
                  value={form.location}
                  onChange={updateField('location')}
                />
              </Field>
              <Field>
                Start Date*
                <Input type="date" value={form.start_date} onChange={updateField('start_date')} required />
              </Field>
              <Field>
                Start Time
                <Input type="time" value={form.start_time} onChange={updateField('start_time')} />
              </Field>
            </>
          ) : null}
          <Field>
            Share Date
            <Input
              type="date"
              value={form.share_date}
              onChange={updateField('share_date')}
              aria-describedby="share-date-help"
            />
            <Helper id="share-date-help">When should communications start?</Helper>
          </Field>
          <Field>
            Requested By
            <Input type="text" value={contactName} disabled aria-readonly />
            <Helper>We’ll use your account name for follow-up.</Helper>
          </Field>
            </Grid>

        <FormDivider />
        <FormSectionTitle>Announcement</FormSectionTitle>
        <Field>
          Announcement verbiage
          <TextArea
            placeholder="What should be said about the event? Include key details and call-to-action."
            value={form.description}
            onChange={updateField('description')}
          />
          <Helper>Optional image (max 5MB).</Helper>
          <FileUploadRow>
            <VisuallyHiddenFile
              id={announcementFileInputId}
              type="file"
              accept="image/*"
              onChange={(e) => {
                void handleAnnouncementImageUpload(e.target.files?.[0]);
                e.target.value = '';
              }}
              disabled={isUploadingImage || isPending || isUpdatingRequest}
            />
            <FileUploadTrigger
              htmlFor={announcementFileInputId}
              $disabled={isUploadingImage || isPending || isUpdatingRequest}
            >
              {isUploadingImage ? 'Uploading…' : 'Choose image'}
            </FileUploadTrigger>
          </FileUploadRow>
          {form.image_url ? (
            <>
              <ImagePreview src={form.image_url} alt="Announcement upload preview" />
              <MiniSecondaryButton
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, image_url: '' }))}
                disabled={isUploadingImage || isPending || isUpdatingRequest}
              >
                Remove image
              </MiniSecondaryButton>
            </>
          ) : null}
        </Field>
        </FormSection>

        {!editingRequestId && !isGeneralAnnouncement ? (
          <TicketToggleCard>
            <TicketToggleTitle>Need a facility request too?</TicketToggleTitle>
            <CheckboxRow>
              <CheckboxInput
                type="checkbox"
                checked={continueToFacilityRequest}
                onChange={(event) => setContinueToFacilityRequest(event.target.checked)}
              />
              <span>After this event is submitted for approval, take me to the facility request form.</span>
            </CheckboxRow>
            <Helper>The facility form opens on the public site so you can finish room, timing, and setup details.</Helper>
          </TicketToggleCard>
        ) : null}

        <FormFooter>
        <ButtonRow>
          <Button type="submit" disabled={isPending || isUpdatingRequest}>
            {isPending || isUpdatingRequest
              ? editingRequestId
                ? 'Saving…'
                : 'Submitting…'
              : editingRequestId
                ? 'Save Changes'
                : 'Submit for Approval'}
          </Button>
          {editingRequestId ? (
            <MiniSecondaryButton
              type="button"
              onClick={() => {
                setForm(initialState);
                setTicketTypes([]);
                setEditingRequestId(null);
                setErrorMessage(null);
                setSuccessMessage(null);
                setContinueToFacilityRequest(false);
              }}
              disabled={isPending || isUpdatingRequest}
            >
              Cancel edit
            </MiniSecondaryButton>
          ) : null}
          <Secondary>We’ll send this to the approvals queue for review.</Secondary>
        </ButtonRow>
        </FormFooter>
        </Form>
        ) : (
          <SubmissionsPanel>
            <PanelTitle>Your submissions</PanelTitle>
            {!requesterId && !contactEmail ? (
              <EmptyState>Please sign in to view your submissions.</EmptyState>
            ) : isLoadingSubmittedRequests ? (
              <EmptyState>Loading your submissions…</EmptyState>
            ) : submittedRequests.length === 0 ? (
              <EmptyState>No submissions yet.</EmptyState>
            ) : (
              <SubmissionList>
                <SubmissionFilters>
                  {statusFilterOptions.map((option) => (
                    <SubmissionFilterButton
                      key={option.key}
                      type="button"
                      data-active={submittedStatusFilter === option.key}
                      onClick={() => setSubmittedStatusFilter(option.key)}
                    >
                      {option.label} ({option.count})
                    </SubmissionFilterButton>
                  ))}
                </SubmissionFilters>
                {filteredSubmittedRequests.length === 0 ? (
                  <EmptyState>No submissions with this status.</EmptyState>
                ) : null}
                {filteredSubmittedRequests.map((request) => (
                  <SubmissionCard key={request.id}>
                    <SubmissionHeader>
                      <div>
                        <SubmissionTitle>{request.title || 'Untitled'}</SubmissionTitle>
                        <SubmissionMeta>
                          Submitted: {formatDisplayDate(request.submitted_at)}
                        </SubmissionMeta>
                      </div>
                      <SubmissionStatus>{request.status || 'pending'}</SubmissionStatus>
                    </SubmissionHeader>
                    <SubmissionGrid>
                      <SubmissionField>
                        <SubmissionLabel>Type</SubmissionLabel>
                        <SubmissionValue>{request.event_type || 'Event'}</SubmissionValue>
                      </SubmissionField>
                      <SubmissionField>
                        <SubmissionLabel>Ticketed Event</SubmissionLabel>
                        <SubmissionValue>{request.is_ticketed ? 'Yes' : 'No'}</SubmissionValue>
                      </SubmissionField>
                      <SubmissionField>
                        <SubmissionLabel>Ministry</SubmissionLabel>
                        <SubmissionValue>{request.ministry || '—'}</SubmissionValue>
                      </SubmissionField>
                      <SubmissionField>
                        <SubmissionLabel>Start Date</SubmissionLabel>
                        <SubmissionValue>{formatDisplayDate(request.start_date)}</SubmissionValue>
                      </SubmissionField>
                      <SubmissionField>
                        <SubmissionLabel>Start Time</SubmissionLabel>
                        <SubmissionValue>{formatDisplayTime(request.start_time)}</SubmissionValue>
                      </SubmissionField>
                      <SubmissionField>
                        <SubmissionLabel>Share Date</SubmissionLabel>
                        <SubmissionValue>{formatDisplayDate(request.share_date)}</SubmissionValue>
                      </SubmissionField>
                      <SubmissionField>
                        <SubmissionLabel>Location</SubmissionLabel>
                        <SubmissionValue>{request.location || '—'}</SubmissionValue>
                      </SubmissionField>
                    </SubmissionGrid>
                    {request.description ? (
                      <SubmissionField>
                        <SubmissionLabel>Announcement Verbiage</SubmissionLabel>
                        <SubmissionDescription>{request.description}</SubmissionDescription>
                      </SubmissionField>
                    ) : null}
                    {request.image_url ? (
                      <SubmissionField>
                        <SubmissionLabel>Announcement Image</SubmissionLabel>
                        <SubmissionImageButton
                          type="button"
                          onClick={() =>
                            setLightboxImage({
                              url: request.image_url || '',
                              alt: `${request.title || 'Submitted event'} announcement`,
                            })
                          }
                          aria-label="Open announcement image"
                        >
                          <SubmissionImagePreview
                            src={request.image_url}
                            alt={`${request.title || 'Submitted event'} announcement`}
                            loading="lazy"
                          />
                        </SubmissionImageButton>
                      </SubmissionField>
                    ) : null}
                    <MiniSecondaryButton type="button" onClick={() => beginEditSubmission(request)}>
                      Edit submission
                    </MiniSecondaryButton>
                  </SubmissionCard>
                ))}
              </SubmissionList>
            )}
          </SubmissionsPanel>
        )}
      </Container>
      {lightboxImage ? (
        <LightboxOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Announcement image preview"
          onClick={() => setLightboxImage(null)}
        >
          <LightboxContent onClick={(event) => event.stopPropagation()}>
            <LightboxCloseButton
              type="button"
              onClick={() => setLightboxImage(null)}
              aria-label="Close image preview"
            >
              ×
            </LightboxCloseButton>
            <LightboxImage src={lightboxImage.url} alt={lightboxImage.alt} />
          </LightboxContent>
        </LightboxOverlay>
      ) : null}
    </Page>
  );
};

export default EventSubmissionPage;

